import { action, observable, makeObservable } from 'mobx'
import type { Location } from 'react-router-dom'

/**
 * NavigationStore - Modern replacement for mobx-react-router's RouterStore
 * Works with React Router v6+ by storing navigation references from hooks
 */
class NavigationStore {
  @observable
  public location: Location | null = null

  private navigateRef: ((to: string | number, options?: any) => void) | null = null

  constructor() {
    makeObservable(this)
  }

  /**
   * Set the navigate function from useNavigate hook
   * Call this from your root component after router is initialized
   */
  @action.bound
  public setNavigate(navigate: (to: string | number, options?: any) => void) {
    this.navigateRef = navigate
  }

  /**
   * Set the current location from useLocation hook
   * Call this from your root component to keep location in sync
   */
  @action.bound
  public setLocation(location: Location) {
    this.location = location
  }

  /**
   * Navigate to a new route (replaces routerStore.push)
   */
  @action.bound
  public push(path: string, state?: any) {
    if (this.navigateRef) {
      this.navigateRef(path, { state, replace: false })
    } else {
      console.error('NavigationStore: navigate function not set. Call setNavigate first.')
    }
  }

  /**
   * Replace current route (replaces routerStore.replace)
   */
  @action.bound
  public replace(path: string, state?: any) {
    if (this.navigateRef) {
      this.navigateRef(path, { state, replace: true })
    } else {
      console.error('NavigationStore: navigate function not set. Call setNavigate first.')
    }
  }

  /**
   * Go back in history
   */
  @action.bound
  public goBack() {
    if (this.navigateRef) {
      this.navigateRef(-1)
    } else {
      console.error('NavigationStore: navigate function not set. Call setNavigate first.')
    }
  }

  /**
   * Go forward in history
   */
  @action.bound
  public goForward() {
    if (this.navigateRef) {
      this.navigateRef(1)
    } else {
      console.error('NavigationStore: navigate function not set. Call setNavigate first.')
    }
  }
}

export default NavigationStore
